<script setup lang="ts">
  import { Button, Typo, Tag, Modal, type TypoVariant } from '@travelata-libs/ui-kit';
  import { formatPhone } from '@travelata-libs/utils';
  import { ref, computed } from 'vue';
  import type { LeadBannerPayload } from './LeadBanner.types';
  import LeadBannerAvatars from './LeadBannerAvatars.vue';
  import LeadBannerForm from './LeadBannerForm.vue';
  import { TAG_RESPONSE_TIME, TAG_EXPERIENCE } from './lib';

  interface LeadBannerProps {
    title: string;
    subtitle?: {
      text: string;
      variant?: `${TypoVariant}`;
    };
    mobile?: boolean;
    modelValue?: LeadBannerPayload;
    isSubmitted?: boolean;
    loading?: boolean;
    userAgreementUrl?: string;
    privacyPolicyUrl?: string;
    dataProcessingUrl?: string;
  }

  interface LeadBannerEmits {
    (e: 'submit', payload: LeadBannerPayload): void;
    (e: 'update:modelValue', value: LeadBannerPayload): void;
  }

  const emit = defineEmits<LeadBannerEmits>();

  const props = withDefaults(defineProps<LeadBannerProps>(), {
    subtitle: undefined,
    mobile: false,
    modelValue: undefined,
    isSubmitted: false,
    loading: false,
    userAgreementUrl: undefined,
    privacyPolicyUrl: undefined,
    dataProcessingUrl: undefined,
  });

  const isModalOpen = ref(false);
  const formRef = ref<InstanceType<typeof LeadBannerForm> | null>(null);

  const maskedPhone = computed(() => formatPhone(formModel.value.phone));

  const containerClasses = computed(() => ({
    'tl-lead-banner': true,
    'tl-lead-banner_size-m': props.size === 'm',
    'tl-lead-banner_mobile': props.mobile,
  }));

  const openModal = () => {
    isModalOpen.value = true;
    setTimeout(() => {
      formRef.value?.focusName();
    }, 300);
  };

  const handleSubmit = (payload: LeadBannerPayload) => {
    if (props.isSubmitted || props.loading) return;
    emit('submit', payload);

    if (props.mobile) {
      isModalOpen.value = false;
    }
  };

  const formModel = computed({
    get: () => props.modelValue ?? { name: '', phone: '' },
    set: (value: LeadBannerPayload) => emit('update:modelValue', value),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div :class="containerClasses" data-test-id="lead-banner">
    <template v-if="!mobile">
      <div v-if="!isSubmitted" class="tl-lead-banner__content">
        <div class="tl-lead-banner__trust">
          <div class="tl-lead-banner__heading">
            <Typo variant="h-3" class="tl-lead-banner__title">{{ title }}</Typo>
            <Typo v-if="subtitle" :variant="subtitle.variant ?? 'body-l-400'" class="tl-lead-banner__subtitle">
              {{ subtitle.text }}
            </Typo>
          </div>

          <div class="tl-lead-banner__social">
            <LeadBannerAvatars />

            <div class="tl-lead-banner__tags">
              <Tag color="green" appearance="primary">{{ TAG_RESPONSE_TIME }}</Tag>
              <Tag color="green" appearance="primary">{{ TAG_EXPERIENCE }}</Tag>
            </div>
          </div>
        </div>

        <LeadBannerForm
          v-model="formModel"
          class="tl-lead-banner__form"
          :loading="loading"
          :user-agreement-url="userAgreementUrl"
          :privacy-policy-url="privacyPolicyUrl"
          :data-processing-url="dataProcessingUrl"
          @submit="handleSubmit"
        />
      </div>

      <div v-else class="tl-lead-banner__success" data-test-id="lead-banner-success">
        <Typo variant="h-3" class="tl-lead-banner__success-title">Заявка отправлена</Typo>
        <Typo variant="body-l-400" class="tl-lead-banner__success-subtitle">
          Вам перезвонит специалист на номер {{ maskedPhone }}
        </Typo>

        <LeadBannerAvatars centered />
      </div>
    </template>

    <template v-else>
      <div v-if="!isSubmitted" class="tl-lead-banner__content-mobile">
        <Typo variant="h-5" class="tl-lead-banner__title">{{ title }}</Typo>

        <LeadBannerAvatars mobile />

        <Button @click="openModal">Отправить заявку</Button>

        <Modal v-if="isModalOpen" :mobile="true" @close="isModalOpen = false">
          <Typo class="tl-lead-banner__modal-header" variant="h-5">Контакты для связи</Typo>
          <LeadBannerForm
            ref="formRef"
            v-model="formModel"
            class="tl-lead-banner__modal-form"
            :loading="loading"
            :user-agreement-url="userAgreementUrl"
            :privacy-policy-url="privacyPolicyUrl"
            :data-processing-url="dataProcessingUrl"
            @submit="handleSubmit"
          />
        </Modal>
      </div>

      <div v-else class="tl-lead-banner__success-mobile">
        <Typo variant="body-l-500" class="tl-lead-banner__success-title">
          Заявка отправлена. Вам перезвонит специалист на номер {{ maskedPhone }}
        </Typo>

        <LeadBannerAvatars mobile centered />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  .tl-lead-banner {
    $root: &;

    background-color: var(--color-bg-primary);
    border-radius: var(--rounding-s);
    box-shadow: var(--shadow-s);
    display: flex;
    justify-content: center;
    padding: var(--spacing-xxxl) var(--spacing-xxl);

    &__content {
      align-items: center;
      display: flex;
      gap: var(--spacing-xxxl);
      justify-content: space-between;
      max-width: 952px;
      width: 100%;
    }

    &__trust {
      display: flex;
      flex: 1 1 420px;
      flex-direction: column;
      height: 100%;
      justify-content: space-between;
      max-width: 420px;
      min-width: 342px;
    }

    &__heading {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
    }

    &__title {
      color: var(--color-text-primary);
    }

    &__subtitle {
      color: var(--color-text-primary);
    }

    &__social {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
    }

    &__tags {
      display: flex;
      gap: var(--spacing-xs);
    }

    &__form {
      display: flex;
      flex-direction: column;
      height: 100%;
      max-width: 420px;
      min-width: 282px;
      width: 100%;
    }

    &__success {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      max-width: 782px;
      text-align: center;
      width: 100%;
    }

    &__success-title {
      color: var(--color-text-primary);
      margin-bottom: var(--spacing-xs);
    }

    &__success-subtitle {
      color: var(--color-text-primary);
      margin-bottom: var(--spacing-xxl);
    }

    #{$root}__success-title {
      margin-bottom: var(--spacing-s);
    }

    &_mobile {
      padding: var(--spacing-l) var(--spacing-m);
    }

    &__modal-header {
      color: var(--color-text-primary);
      margin-bottom: var(--spacing-l);
      text-align: center;
    }

    &__content-mobile {
      align-items: center;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
      text-align: center;
    }

    &__modal-form {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
      padding: 0 var(--spacing-m);
    }

    &__success-mobile {
      align-items: center;
      display: flex;
      flex-direction: column;
      text-align: center;
    }
  }
</style>
